/**
 * QueuesHome — the Queues tab of the Queues section (route `queues`), on the
 * Events-section list paradigm; the LIST view matches Event Hubs (full-width
 * card table, checkbox selection + bulk bar, status edge, identity block).
 * CREATE runs through the Journey designer (`newjourneys?new=queue`, 2026-08-25 —
 * the same entry-point pattern as Add Event); EDIT opens the same designer on the
 * queue's own journey document (`newjourneys?journey=queue:<id>`, 2026-08-26 —
 * the standalone queue wizard was removed).
 * Queue types are development-defined — no admin management surface.
 * WRITES are AWAITED typed ops (no fake interactivity).
 */
import { useMemo, useState, type CSSProperties } from "react";
import { alpha, Btn, Card, Checkbox, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, FilterBar, FilterDropdown, EmptyState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID, useRowSelection, BulkActionBar } from "../../shell/list-composites";
import { useIsMobile } from "../../shell/useResponsive";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useWfResource, useBusy } from "../workforce/useWorkforce";
import { LocationFilter, WfPending } from "../workforce/wf-shared";
import { listQueues, listQueueTypes, createQueue, updateQueue, setQueueStatus, type QueueSummary, type QueueType, type QueueStatus, type QueueListPage } from "./queues-api";
import { qHoursLabel } from "./queue-hours";
import { queuesSectionTabs, useQueueSectionCounts, SORT_ARROW } from "./queues-section";

import { useT } from "@v0y4ge/i18n-runtime";
const statusMeta = (t: (key: string, fallback: string) => string): Record<QueueStatus, { tone: "success" | "warn" | "neutral"; label: string; edge: string }> => ({
  open: { tone: "success", label: t("queues.home.statusOpen", "Open"), edge: T.green1 },
  paused: { tone: "warn", label: t("queues.home.statusPaused", "Paused"), edge: T.orange1 },
  closed: { tone: "neutral", label: t("queues.home.statusClosed", "Closed"), edge: T.n4 },
});
const columnDefs = (t: (key: string, fallback: string) => string): { k: string; label: string }[] => [
  /* "Type" (quick vs standard) removed as a COLUMN 2026-09-08 (operator) — it is
     assigned positionally by the seed layer rather than authored per desk, so it
     carried no information a user could act on. typeId still drives the row tile
     colour and the card-view pill, and the Any-type filter still works. */
  { k: "name", label: t("queues.home.colQueue", "Queue") }, { k: "status", label: t("queues.home.colStatus", "Status") },
  { k: "waiting", label: t("queues.home.colWaiting", "Waiting") }, { k: "avgWait", label: t("queues.home.colAvgWait", "Avg wait") }, { k: "longest", label: t("queues.home.colLongest", "Longest") },
  { k: "staffed", label: t("queues.home.colStaffed", "Staffed") }, { k: "hours", label: t("queues.home.colHours", "Hours") },
];
const thStyle: CSSProperties = { textAlign: "left", padding: "10px 16px", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" };
const tdStyle: CSSProperties = { padding: "14px 16px", verticalAlign: "middle" };

export default function QueuesHome() {
  const t = useT();
  const STATUS_META = statusMeta(t);
  const COLUMNS = columnDefs(t);
  const nav = useNav();
  const isMobile = useIsMobile();
  const counts = useQueueSectionCounts();
  const [locationId, setLocationId] = useState<string | null>(null);
  const { data, loading, error, reload } = useWfResource<QueueListPage>(`queues|${locationId ?? "all"}`, () => listQueues(locationId), { pollMs: 30_000 });
  const { data: typesData } = useWfResource<{ types: QueueType[] }>("qtypes", () => listQueueTypes());
  const { busy, run } = useBusy();
  const [view, setView] = useViewMode("queues", isMobile ? "grid" : "list");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [sort, setSort] = useState("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const types = typesData?.types ?? [];
  const queues = data?.data ?? [];
  const typeLabel = (id: string) => types.find((t) => t.id === id)?.label ?? id;

  const onHeaderSort = (k: string) => {
    if (sort === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setSortDir(["waiting", "avgWait", "longest"].includes(k) ? "desc" : "asc"); }
  };
  const filtered = useMemo(() => {
    const qy = query.trim().toLowerCase();
    let rows = queues.filter((q) =>
      (!qy || q.name.toLowerCase().includes(qy) || (q.locationName ?? "").toLowerCase().includes(qy) || (q.services ?? []).join(" ").toLowerCase().includes(qy)) &&
      (!statusFilter || STATUS_META[q.status].label === statusFilter) &&
      (!typeFilter || typeLabel(q.typeId) === typeFilter));
    const val = (q: QueueSummary): string | number => sort === "status" ? q.status
      : sort === "waiting" ? q.waiting : sort === "avgWait" ? q.avgWaitMins : sort === "longest" ? q.longestWaitMins
      : sort === "staffed" ? q.staffed : sort === "hours" ? qHoursLabel(q.hours) : q.name.toLowerCase();
    rows = [...rows].sort((a, b) => { const x = val(a), y = val(b); const base = x < y ? -1 : x > y ? 1 : 0; return sortDir === "desc" ? -base : base; });
    return rows;
  }, [queues, query, statusFilter, typeFilter, sort, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const selection = useRowSelection(filtered.map((q) => q.id));

  const totals = useMemo(() => ({
    waiting: queues.reduce((s, q) => s + q.waiting, 0),
    avgWait: queues.length ? Math.round(queues.reduce((s, q) => s + q.avgWaitMins, 0) / queues.length) : 0,
    longest: queues.reduce((s, q) => Math.max(s, q.longestWaitMins), 0),
    open: queues.filter((q) => q.status === "open").length,
  }), [queues]);

  const changeStatus = (q: QueueSummary, status: QueueStatus) => run(`st:${q.id}`, async () => {
    await setQueueStatus(q.id, status);
    const statusLabel = STATUS_META[status].label.toLowerCase();
    toast({ tone: "success", title: t("queues.home.toastStatusChangedTitle", "Queue {status}").replace("{status}", statusLabel), subtitle: t("queues.home.toastStatusChangedSubtitle", "“{name}” is now {status}.").replace("{name}", q.name).replace("{status}", statusLabel) }); // ui-config — "{…}" strings are message-placeholder tokens, not copy
    reload();
  });
  const bulkStatus = (status: QueueStatus) => run(`bulk:${status}`, async () => {
    const targets = filtered.filter((q) => selection.selected.has(q.id) && q.status !== status);
    await Promise.all(targets.map((q) => setQueueStatus(q.id, status)));
    toast({ tone: "success", title: t("queues.home.toastBulkStatusChangedTitle", { n: targets.length, status: STATUS_META[status].label.toLowerCase() }) });
    selection.clear();
    reload();
  });
  const clone = (q: QueueSummary) => run(`cl:${q.id}`, async () => {
    const created = await createQueue({ name: `${q.name} (copy)`, typeId: q.typeId, locationId: q.locationId, services: q.services, slaTargetMins: q.slaTargetMins, ticketPrefix: q.ticketPrefix });
    // The embedded journey travels with the clone (deep copy — the queues diverge freely afterwards).
    await updateQueue(created.id, { hours: q.hours, assignedStaff: q.assignedStaff, resources: q.resources, needed: q.needed, journey: q.journey ? JSON.parse(JSON.stringify(q.journey)) : null, journeySource: q.journey ? t("queues.home.journeySourceCopiedFrom", "Copied from “{name}”").replace("{name}", q.name) : q.journeySource });
    toast({ tone: "success", title: t("queues.home.toastQueueClonedTitle", "Queue cloned"), subtitle: t("queues.home.toastQueueClonedSubtitle", "“{name} (copy)” created — it starts closed until you open it.").replace("{name}", q.name) }); // ui-config — "{…}" strings are message-placeholder tokens, not copy
    reload();
  });
  const copyJoin = (q: QueueSummary) => { // ui-local — clipboard only
    navigator.clipboard?.writeText(`${location.origin}/queue/join?q=${q.id}`).then(
      () => toast({ tone: "success", title: t("queues.home.toastJoinLinkCopiedTitle", "Join link copied"), subtitle: q.name }),
      () => toast({ tone: "danger", title: t("queues.home.toastCopyFailedTitle", "Couldn't copy the link") }));
  };

  const moreItems = (q: QueueSummary) => [
    ...(can("queuehealth.read") ? [{ icon: <Icon.chart size={14} />, label: t("queues.home.menuQueueHealth", "Queue health"), onClick: () => nav.go("queues.health", { q: q.id }) }] : []),
    { icon: <Icon.copy size={14} />, label: t("queues.home.menuCopyJoinLink", "Copy join link"), onClick: () => copyJoin(q) },
    ...(can("queue.serve") ? [{ icon: <Icon.check size={14} />, label: t("queues.home.menuOpenConcierge", "Open Concierge"), onClick: () => nav.go("queues.console", { q: q.id }) }] : []),
    ...(can("queues.publish") ? [
      "divider" as const,
      q.status === "open"
        ? { icon: <Icon.clock size={14} />, label: t("queues.home.menuPauseQueue", "Pause queue"), onClick: () => changeStatus(q, "paused") }
        : { icon: <Icon.refresh size={14} />, label: q.status === "paused" ? t("queues.home.menuResumeQueue", "Resume queue") : t("queues.home.menuOpenQueue", "Open queue"), onClick: () => changeStatus(q, "open") },
      ...(q.status !== "closed" ? [{ icon: <Icon.x size={14} />, label: t("queues.home.menuCloseQueue", "Close queue"), danger: true, onClick: () => changeStatus(q, "closed") }] : []),
    ] : []),
  ];
  const actions = (q: QueueSummary) => ({
    // Eye action = the customer-facing journey preview — the SAME view as the
    // Live tab's Preview button (interactive join flow).
    onView: () => nav.go("queue.join", { q: q.id }),
    viewLabel: t("queues.home.actionViewPreview", "View preview"),
    onEdit: can("queues.write") ? () => nav.go("newjourneys", { journey: `queue:${q.id}` }) : undefined,
    editLabel: t("queues.home.actionEditQueue", "Edit queue"),
    onDuplicate: can("queues.write") ? () => clone(q) : undefined,
    duplicateLabel: t("queues.home.actionCloneQueue", "Clone queue"),
    moreHeader: q.name,
    moreItems: moreItems(q),
  });

  const filtersActive = !!(query || statusFilter || typeFilter);
  const openRow = (q: QueueSummary) => can("queues.write") ? nav.go("newjourneys", { journey: `queue:${q.id}` }) : nav.go("queues.console", { q: q.id });

  const listView = (
    <Card style={{ padding: 0 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
            <th style={{ ...thStyle, width: 36, paddingRight: 0, cursor: "default", boxSizing: "content-box" }}>
              <Checkbox checked={selection.allChecked} indeterminate={selection.someChecked} onChange={selection.toggleAll} />
            </th>
            {COLUMNS.map((c) => (
              <th key={c.k} style={thStyle} onClick={() => onHeaderSort(c.k)} aria-sort={sort === c.k ? (sortDir === "asc" ? "ascending" : "descending") : undefined}>
                {c.label}{SORT_ARROW(sort === c.k, sortDir)}
              </th>
            ))}
            <th style={{ ...thStyle, cursor: "default", width: 150 }}>{t('queues.home.text')}</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((q, i) => {
            const overSla = q.avgWaitMins > q.slaTargetMins && q.status === "open";
            return (
              <tr key={q.id} data-testid="queues-table-row" data-id={q.id} onClick={() => openRow(q)}
                style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: selection.selected.has(q.id) ? T.p4 : T.white, borderLeft: `3px solid ${STATUS_META[q.status].edge}` }}>
                <td style={{ ...tdStyle, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selection.selected.has(q.id)} onChange={() => selection.toggle(q.id)} />
                </td>
                <td style={tdStyle}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: q.typeId === "quick" ? alpha(T.tDarkBlue, 90) : alpha(T.p1, 90), color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.users size={18} /></div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: T.n1, fontWeight: 600 }}>{q.name}</div>
                      <div style={{ color: T.soft, fontSize: 11 }}>{q.locationName ?? ""}{(q.services ?? []).length ? /* TODO(i18n): replace template-literal with t('queues.home.text2', { ... }) */` · ${(q.services ?? []).join(", ")}` : ""}</div>
                    </div>
                  </div>
                </td>
                <td style={tdStyle}><Pill tone={STATUS_META[q.status].tone} dot>{STATUS_META[q.status].label}</Pill></td>
                <td style={{ ...tdStyle, color: T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{q.waiting}</td>
                <td style={{ ...tdStyle, fontVariantNumeric: "tabular-nums" }}>
                  <div style={{ color: overSla ? T.orange1 : T.n1, fontWeight: 600 }}>{q.avgWaitMins}{t('queues.home.text3')}</div>
                  <div style={{ fontSize: 11, color: T.soft }}>{t('queues.home.text4')}{" "}{q.slaTargetMins}{t('queues.home.text3')}</div>
                </td>
                <td style={{ ...tdStyle, color: q.longestWaitMins > q.slaTargetMins * 1.5 ? T.red1 : T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{q.longestWaitMins}{t('queues.home.text3')}</td>
                <td style={{ ...tdStyle, color: q.staffed < q.needed ? T.red1 : T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{q.staffed}/{q.needed}</td>
                <td style={{ ...tdStyle, color: T.soft, fontSize: 12, maxWidth: 220 }}>{qHoursLabel(q.hours)}</td>
                <td style={tdStyle} onClick={(e) => e.stopPropagation()}><RowActions {...actions(q)} testid="queues-row" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );

  const cardView = (
    <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 14 }}>
      {filtered.map((q) => {
        const overSla = q.avgWaitMins > q.slaTargetMins && q.status === "open";
        return (
          <Card key={q.id} data-testid="queues-card" data-id={q.id} style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer" }} onClick={() => openRow(q)}>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, flex: 1, borderLeft: `3px solid ${STATUS_META[q.status].edge}` }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q.name}</div>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{q.locationName ?? ""}{(q.services ?? []).length ? /* TODO(i18n): replace template-literal with t('queues.home.text2', { ... }) */` · ${(q.services ?? []).join(", ")}` : ""}</div>
                  <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}><Icon.clock size={11}/> {qHoursLabel(q.hours)}</div>
                </div>
                <Pill tone={q.typeId === "quick" ? "info" : "primary"}>{typeLabel(q.typeId)}</Pill>
                <Pill tone={STATUS_META[q.status].tone} dot>{STATUS_META[q.status].label}</Pill>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12.5, color: T.soft, flexWrap: "wrap" }}>
                <span><b style={{ color: T.n1, fontFamily: T.fontMono, fontSize: 15 }}>{q.waiting}</b> {t('queues.home.text5')}</span>
                <span>{t('queues.home.text6')}{" "}<b style={{ color: overSla ? T.orange1 : T.n1, fontFamily: T.fontMono }}>{q.avgWaitMins}{t('queues.home.text3')}</b> / {q.slaTargetMins}{t('queues.home.text3')}</span>
                <span>{t('queues.home.text7')}{" "}<b style={{ color: q.longestWaitMins > q.slaTargetMins * 1.5 ? T.red1 : T.n1, fontFamily: T.fontMono }}>{q.longestWaitMins}{t('queues.home.text3')}</b></span>
                <span style={{ color: q.staffed < q.needed ? T.red1 : T.soft }}>{q.staffed}/{q.needed} {t('queues.home.text8')}</span>
              </div>
            </div>
            <CardActionBar left={<span>{q.servedToday} {t('queues.home.text9')}</span>} {...actions(q)} testid="queues-card" />
          </Card>
        );
      })}
    </div>
  );

  return (
    <Screen rail="q-board" tabs={queuesSectionTabs(nav, "queues", counts)} activeTab="queues">
      <PageHeader title={t('queues.home.title4')} subtitle={t('queues.home.subtitle')} breadcrumb={null}
        actions={<>
          {can("queues.write") ? <Btn icon={<Icon.plus size={14}/>} onClick={() => nav.go("newjourneys", { new: "queue" })} testid="queues-new-btn">{t('queues.home.text10')}</Btn> : null}
        </>} />
      <DataSourceStrip screen="queues" ops={["listQueues", "listQueueTypes", "createQueue", "updateQueue", "setQueueStatus"]} />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t('queues.home.label12')} value={totals.waiting} icon={<Icon.users size={14}/>} accent={totals.waiting > 0 ? T.orange1 : T.green1} />
        <StatTile label={t('queues.home.label8')} value={`${totals.avgWait}m`} icon={<Icon.clock size={14}/>} accent={T.tDarkBlue} />
        <StatTile label={t('queues.home.label13')} value={`${totals.longest}m`} icon={<Icon.warn size={14}/>} accent={totals.longest > 20 ? T.red1 : T.n2} />
        <StatTile label={t('queues.home.label14')} value={`${totals.open}/${queues.length}`} icon={<Icon.list size={14}/>} accent={T.p1} />
      </div>
      <FilterBar>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('queues.home.placeholder')} aria-label={t('queues.home.ariaLabel')} data-testid="queues-search-input"
          style={{ height: 34, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 13, fontFamily: T.font, width: 240 }} />
        <FilterDropdown label={statusFilter || t("queues.home.filterAnyStatus", "Any status")} active={!!statusFilter} options={[t("queues.home.statusOpen", "Open"), t("queues.home.statusPaused", "Paused"), t("queues.home.statusClosed", "Closed")]} onPick={setStatusFilter} onClear={() => setStatusFilter(null)} />
        <FilterDropdown label={typeFilter || t("queues.home.filterAnyType", "Any type")} active={!!typeFilter} options={types.map((t) => t.label)} onPick={setTypeFilter} onClear={() => setTypeFilter(null)} />
        <LocationFilter value={locationId} onChange={setLocationId} />
        {filtersActive ? <button onClick={() => { setQuery(""); setStatusFilter(null); setTypeFilter(null); }} style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: "transparent", color: T.p1, border: "none", cursor: "pointer", fontFamily: T.font }}>{t('queues.home.text11')}</button> : null}
        <span style={{ marginLeft: "auto" }} />
        <ViewToggle mode={view} onChange={setView} testid="queues-view" />
      </FilterBar>

      {can("queues.publish") ? (
        <BulkActionBar count={selection.selected.size} noun="queue" onClear={selection.clear}
          actions={[
            { label: t("queues.home.bulkOpen", "Open"), icon: <Icon.refresh size={12} />, onClick: () => bulkStatus("open") },
            { label: t("queues.home.bulkPause", "Pause"), icon: <Icon.clock size={12} />, onClick: () => bulkStatus("paused") },
            { label: t("queues.home.bulkClose", "Close"), icon: <Icon.x size={12} />, danger: true, onClick: () => bulkStatus("closed") },
          ]} />
      ) : null}

      <div style={{ padding: "0 24px 24px", flex: 1, overflow: "auto" }}>
        {error ? <WfPending screen="queues" opName="listQueues" error={error} onRetry={reload} />
        : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t('queues.home.text12')}</div>
        : filtered.length === 0 ? (
          filtersActive
            ? <EmptyState icon={<Icon.list size={20}/>} title={t("queues.home.emptyFilteredTitle", "No queues match these filters")} body={t("queues.home.emptyFilteredBody", "Try clearing a filter or widening the search.")} action={<Btn variant="secondary" onClick={() => { setQuery(""); setStatusFilter(null); setTypeFilter(null); }}>{t("queues.home.text11", "Clear filters")}</Btn>} />
            : <EmptyState icon={<Icon.list size={20}/>} title={t("queues.home.emptyNoneTitle", "No queues at this location")} body={t("queues.home.emptyNoneBody", "Create a queue to start guiding walk-in customers through their visit.")} action={can("queues.write") ? <Btn onClick={() => nav.go("newjourneys", { new: "queue" })}>{t("queues.home.text10", "Add Queue")}</Btn> : undefined} />
        ) : view === "list" && !isMobile ? listView : cardView}
      </div>
    </Screen>
  );
}
